/* eslint-disable no-case-declarations */
import Vue from 'vue';

const menuSelector = '[role="menu"]';
const menubarSelector = '[role="menubar"]';
const menuItemSelector = '[role="menuitem"]';

const focusMenuItem = (item) => {
  item.focus();
  item.tabIndex = 0;
};

const bindControlEvents = (previousKey, nextKey, enterToOpen = false) => (menu, items) => {
  const first = items[0];
  const last = items[items.length - 1];
  let currentIndex = 0;

  const stopPreventKeys = [
    previousKey, nextKey, 'home', 'end',
  ];

  menu.addEventListener('keydown', (event) => {
    const item = items[currentIndex];
    const key = event.key.toLowerCase();

    if (stopPreventKeys.includes(key)) {
      event.stopPropagation();
      event.preventDefault();
    }

    switch (key) {
      case 'enter':
        if (enterToOpen) {
          menu.click();
        }
        break;
      case 'home':
        currentIndex = 0;
        focusMenuItem(first);
        break;
      case 'end':
        currentIndex = items.length - 1;
        focusMenuItem(last);
        break;
      case previousKey:
      case nextKey:
        item.tabIndex = -1;
        const direction = key === previousKey ? -1 : 1;
        const fallback = key === previousKey ? last : first;
        currentIndex = (currentIndex + direction) % items.length;
        if (currentIndex < 0) {
          currentIndex = items.length - 1;
        }

        const nextItem = items[currentIndex] || fallback;
        if (nextItem) {
          focusMenuItem(nextItem);
        }
        break;
      default:
        break;
    }
  });
};

const bindMenuEvents = bindControlEvents('arrowup', 'arrowdown', true);
const bindMenuBarEvents = bindControlEvents('arrowleft', 'arrowright');

const bindMenuItemEvents = (item) => {
  item.tabIndex = -1;
  item.addEventListener('keydown', (event) => {
    switch (event.key.toLowerCase()) {
      case 'enter':
        item.click();
        event.stopPropagation();
        event.preventDefault();
        break;
      default:
        break;
    }
  });
};

const bindMenuBarItemEvents = (item, index) => {
  item.tabIndex = index > 0 ? -1 : 0;
  item.addEventListener('keydown', (event) => {
    switch (event.key.toLowerCase()) {
      case 'enter':
        item.click();
        event.stopPropagation();
        event.preventDefault();
        break;
      case 'arrowdown':
        if (item.getAttribute('aria-haspopup') === 'true') {
          item.click();
          event.stopPropagation();
          event.preventDefault();
        }
        break;
      default:
        break;
    }
  });
};
/**
 * Binds keyboard navigation events to the element, considering all descendants
 * with `role="menuitem"` as the items of this menu.
 *
 * The navigation is implemented according to the WAI-ARIA Menu pattern:
 * https://www.w3.org/TR/wai-aria-practices-1.1/#menu
 *
 * Make sure the bound element has `role="menu"`.
 */
export const accessibleMenu = {
  bind(menu) {
    const items = Array.from(menu.querySelectorAll(menuItemSelector));
    if (!items || items.length === 0) {
      return;
    }

    items.forEach(bindMenuItemEvents);
    bindMenuEvents(menu, items);
  },
};

/**
 * Binds keyboard navigation events to the element, considering all descendants
 * with `role="menuitem"`, which are not descendants
 * of a child menu/menubar as the items of this menu.
 *
 * The navigation is implemented according to the WAI-ARIA Menubar pattern:
 * https://www.w3.org/TR/wai-aria-practices-1.1/#menu
 *
 * Make sure the bound element has `role="menubar"`.
 */
export const accessibleMenubar = {
  bind(menubar) {
    const items = Array
      .from(menubar.querySelectorAll(menuItemSelector))
      .filter((item) => item.closest([menuSelector, menubarSelector].join(',')) === menubar);
    if (!items || items.length === 0) {
      return;
    }

    items.forEach(bindMenuBarItemEvents);
    bindMenuBarEvents(menubar, items);
  },
};

Vue.directive('accessible-menu', accessibleMenu);
Vue.directive('accessible-menubar', accessibleMenubar);
